import {useEffect, useState} from 'react'
import {Modal, Box} from '@mui/material'
import {KTSVG} from '../../../../_metronic/helpers'
import {SHOW_ERROR, SHOW_SUCCESS} from '../../../../util'
import {
  addEnrichmentPhrasesRequest,
  updateEnrichmentPhraseRequest,
} from '../../../../api/endpoints/enrichmentPhrases'
import {PhraseRow} from '../Howie'

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 700,
  maxWidth: '95vw',
  bgcolor: 'white',
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflow: 'auto',
}

const clean = (text: string) => text.replace(/\s+/g, ' ').trim()

const errorMessage = (res: any, fallback: string) => {
  const payload = res?.data ?? res?.response?.data
  const message = payload?.message
  if (typeof message === 'string' && message) return message
  if (message && typeof message === 'object') return Object.values(message).flat().join(', ')
  return fallback
}

const isOk = (res: any) => {
  const payload = res?.data ?? res?.response?.data
  return res?.status >= 200 && res?.status < 300 && payload?.success !== false
}

interface Props {
  row: PhraseRow | null
  onClose: () => void
  onSaved: () => void
}

const PhrasesModal = ({row, onClose, onSaved}: Props) => {
  const [edited, setEdited] = useState<Record<string, string>>({})
  const [newPhrases, setNewPhrases] = useState<string[]>([])
  const [input, setInput] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setEdited({})
    setNewPhrases([])
    setInput('')
    setError('')
  }, [row])

  if (!row) return null

  const taken = (text: string, exceptId?: string | number) => {
    const key = text.toLowerCase()
    const existing = row.phrases.some(
      (p) => p.id !== exceptId && clean(edited[p.id] ?? p.content).toLowerCase() === key
    )
    return existing || newPhrases.some((p) => p.toLowerCase() === key)
  }

  const handleAdd = () => {
    setError('')
    const text = clean(input)
    if (!text) return
    if (taken(text)) {
      setError('This phrase is already in the list')
      return
    }
    setNewPhrases((prev) => [...prev, text])
    setInput('')
  }

  const changes = row.phrases.filter((p) => {
    const value = edited[p.id]
    return value !== undefined && clean(value) !== clean(p.content)
  })

  const hasEmptyEdit = changes.some((p) => !clean(edited[p.id]))
  const canSave = (changes.length > 0 || newPhrases.length > 0) && !hasEmptyEdit

  const onSave = async () => {
    setSaving(true)
    let failed = 0
    try {
      if (newPhrases.length > 0) {
        const res: any = await addEnrichmentPhrasesRequest({
          service_id: row.serviceId,
          phrases: newPhrases,
        })
        if (!isOk(res)) {
          failed++
          SHOW_ERROR(true, errorMessage(res, 'Failed to add phrases'))
        }
      }
      for (const p of changes) {
        const res: any = await updateEnrichmentPhraseRequest(clean(edited[p.id]), p.id)
        if (!isOk(res)) {
          failed++
          SHOW_ERROR(true, errorMessage(res, `Failed to update "${p.content}"`))
        }
      }
      if (failed === 0) {
        SHOW_SUCCESS(true, 'Phrases saved')
        onSaved()
        onClose()
      } else {
        onSaved()
      }
    } catch (e: any) {
      SHOW_ERROR(true, 'Failed to save phrases')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={!!row} onClose={onClose} aria-labelledby='howie-phrases-title'>
      <Box sx={style}>
        <div className='modal-header'>
          <div>
            <h2 className='fw-bolder mb-1' id='howie-phrases-title'>
              {row.subchannel || 'Trigger phrases'}
            </h2>
            <div className='text-muted fs-7'>{row.channel}</div>
          </div>
          <div
            onClick={onClose}
            className='btn btn-icon btn-sm btn-active-icon-primary'
            style={{cursor: 'pointer'}}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
          </div>
        </div>

        <div className='mt-6'>
          <label className='form-label'>Add phrases</label>
          <div className='d-flex gap-3'>
            <input
              className='form-control form-control-solid'
              placeholder='e.g. my pipes are leaking'
              value={input}
              maxLength={255}
              onChange={(e) => {
                setInput(e.target.value)
                if (error) setError('')
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAdd()
                }
              }}
            />
            <button type='button' className='btn btn-primary' onClick={handleAdd}>
              Add
            </button>
          </div>
          {error ? (
            <div className='text-danger mt-2'>{error}</div>
          ) : (
            <div className='form-text'>
              Write it the way a member would say it. Press Enter to add more than one.
            </div>
          )}
          {newPhrases.length > 0 && (
            <div className='d-flex flex-wrap gap-2 mt-4'>
              {newPhrases.map((p) => (
                <span
                  key={p}
                  className='badge badge-light-primary d-inline-flex align-items-center'
                >
                  <span className='me-2'>{p}</span>
                  <button
                    type='button'
                    className='btn btn-sm btn-icon btn-light-danger'
                    onClick={() => setNewPhrases((prev) => prev.filter((n) => n !== p))}
                    aria-label={`Remove ${p}`}
                  >
                    <i className='bi bi-x-lg'></i>
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className='separator my-6'></div>

        <label className='form-label'>Existing phrases ({row.phrases.length})</label>
        {row.phrases.length === 0 ? (
          <div className='text-muted'>No phrases yet. Add some above.</div>
        ) : (
          <div className='d-flex flex-column gap-2'>
            {row.phrases.map((p) => {
              const value = edited[p.id] ?? p.content
              const changed = clean(value) !== clean(p.content)
              return (
                <input
                  key={p.id}
                  className={`form-control form-control-sm form-control-solid ${
                    changed ? 'border border-primary' : ''
                  } ${changed && !clean(value) ? 'is-invalid' : ''}`}
                  value={value}
                  maxLength={255}
                  onChange={(e) => setEdited((prev) => ({...prev, [p.id]: e.target.value}))}
                />
              )
            })}
          </div>
        )}

        <div className='d-flex justify-content-end gap-3 mt-8'>
          <button type='button' className='btn btn-light' onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type='button'
            className='btn btn-primary'
            disabled={!canSave || saving}
            onClick={onSave}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </Box>
    </Modal>
  )
}

export default PhrasesModal
